<template>
  <div class="game-list-page">
    <SiteHeader :config="configData" :query-string="queryString" />

    <!-- Larger Banner for Mobile when enabled -->
    <LargerBanner
      v-if="isEnableLargeBanner && getDeviceType() === 'mobile'"
      :slides="mobileBannerSlides"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <!-- Default Banner when LargerBanner is not enabled -->
    <Banner
      v-else-if="!isEnableLargeBanner"
      :slides="slides"
      :image-url="imageUrl"
      :query-string="queryString"
    />

    <div class="page-header">
      <h1>{{ $t('bestSellingGames') }}</h1>
    </div>

    <!-- 使用新的GameList組件 -->
    <GameList
      :games="games"
      :image-url="imageUrl"
      :query-string="queryString"
    />

    <footer class="footer">
      <div class="footer-content">
        <a :href="`${h365Url}${langMap}${queryString ? '?' + queryString : ''}`">
          <div class="footer-logo">
            <img :src="`${imageUrl}/${logoImage}`" />
          </div>
        </a>
        <div class="footer-txt" v-html="$t('footerMessage')"></div>
      </div>
      <div class="copyright">copyright ©H365 All rights reserved.</div>
    </footer>
  </div>
</template>

<script>
import { configurationLocale } from '@/utils/navigation'
import SiteHeader from '@/components/SiteHeader.vue'
import GameList from '@/components/GameList.vue'
import LargerBanner from '@/components/LargerBanner.vue'
import Banner from '@/components/Banner.vue'
import { getCloudStorage } from '@/plugin/axios'
import qs from 'qs'

export default {
  name: 'GameListPage',
  components: {
    SiteHeader,
    GameList,
    LargerBanner,
    Banner
  },
  props: {
    isEnableLargeBanner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      queryString: '',
      configData: {},
      games: [],
      slides: [],
      mobileBannerSlides: []
    }
  },
  computed: {
    h365Url() {
      return process.env.VUE_APP_H365_URL || 'https://h365.games'
    },
    langMap() {
      return this.$i18n.locale === 'zh-CN' ? '/zh-CN' : this.$i18n.locale === 'zh-TW' ? '/zh-TW' : ''
    },
    imageUrl() {
      return process.env.VUE_APP_IMAGE_URL || 'https://gs-api.zuczxt.cn/storage/v1/b/h365-landing-page/o/428/h365-landing-page'
    },
    logoImage() {
      if (this.configData?.logo) {
        const width = this.getDeviceType() === 'desktop' ? 140 : 119
        return `${width}/h365-landing-page/config/minisite/image/${this.configData.logo}`
      }
      return '119/h365-landing-page/config/minisite/image/logo.png'
    }
  },
  methods: {
    getDeviceType() {
      const width = window.innerWidth || document.body.getBoundingClientRect().width
      if (width <= 768) return 'mobile'
      if (width <= 1024) return 'tablet'
      return 'desktop'
    }
  },
  async created() {
    this.queryString = window.location.search.slice(1)
    const query = qs.parse(this.queryString)
    const filename = query.utm_source ? query.utm_source : 'default'
    const url = encodeURIComponent(`config/minisite/${filename}.json`)

    try {
      const timestamp = Math.floor(Date.now() / (5 * 600))

      const { data } = await getCloudStorage.get(url, { params: { alt: 'media', t: timestamp } })
      if (data) {
        this.configData = data
        if (this.$i18n.messages[configurationLocale(data.lang)]) this.$i18n.locale = configurationLocale(data.lang)

        // 處理遊戲數據的圖片路徑，只取前6款
        if (data.games && data.games.length > 0) {
          const width = this.getDeviceType() === 'desktop' ? 256 : 384
          this.games = data.games.slice(0, 6).map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        else
          this.games = []

        // 處理 slides 數據的圖片路徑
        if (data.slides && data.slides.length > 0) {
          const width = this.getDeviceType() === 'desktop' ? 1360 : 428
          this.slides = data.slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        else
          this.slides = []

        // 處理 mobile_banner_slides 數據的圖片路徑
        if (data.mobile_banner_slides && data.mobile_banner_slides.length > 0) {
          const width = 428 // Mobile only, fixed width
          this.mobileBannerSlides = data.mobile_banner_slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        else
          this.mobileBannerSlides = []

        if (data.lang && this.$i18n.messages[data.lang])
          this.$i18n.locale = data.lang
      }
    }
    catch (error) {
      // Fallback to default config
      const fallbackUrl = encodeURIComponent('config/minisite/default.json')
      const timestamp = Math.floor(Date.now() / (5 * 600))

      try {
        const { data } = await getCloudStorage.get(fallbackUrl, { params: { alt: 'media', t: timestamp } })
        if (data) {
          this.configData = data
          if (this.$i18n.messages[configurationLocale(data.lang)]) this.$i18n.locale = configurationLocale(data.lang)

          // 處理遊戲數據的圖片路徑，只取前6款
          if (data.games && data.games.length > 0) {
            const width = this.getDeviceType() === 'desktop' ? 256 : 384
            this.games = data.games.slice(0, 6).map(item => ({
              ...item,
              image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
            }))
          }
          else
            this.games = []

          this.slides = data.slides || []
          this.mobileBannerSlides = data.mobile_banner_slides || []

          if (data.lang && this.$i18n.messages[data.lang])
            this.$i18n.locale = data.lang
        }
      }
      catch (fallbackError) {
        // Handle fallback error silently
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.game-list-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
}

.page-header {
  text-align: center;
  padding: 80px 20px 40px;

  h1 {
    font-size: 2.5rem;
    font-weight: bold;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

    &::after {
      content: '';
      display: block;
      width: 60px;
      height: 3px;
      background: #007bff;
      margin: 20px auto 0;
      border-radius: 2px;
    }
  }
}

.footer {
  background: rgba(0, 0, 0, 0.3);
  padding: 40px 20px 20px;
  margin-top: 60px;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.footer-logo img {
  height: 30px;
  width: auto;
}

.footer-txt {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

.copyright {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
}

@media (max-width: 768px) {
  .page-header {
    padding: 80px 15px 30px;

    h1 {
      font-size: 2rem;
    }
  }

  .footer-content {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
}
</style>